<template>
  <div>
    <div class="button-row">
      <input v-show="false" class="file-selector" type="file" ref="file" accept=".csv" @change="readFile($event.target.files)">
      <button type="button" class="e-button:word" @click="$refs.file.click()">
        <svg class="ex-icon">
          <use :xlink:href="'#file'"></use>
        </svg>&nbsp;Overlay
      </button>
      <BaseButton :disabled="!enableCeButton" type=":word" @baseButtonClick="loadCeProps">
        <svg class="ex-icon">
          <use :xlink:href="'#download'"></use>
        </svg>
        &nbsp;Get CE Props
      </BaseButton>
      <button type="submit" class="e-button:word" form="seed-form">
        <svg class="ex-icon">
          <use :xlink:href="'#save'"></use>
        </svg>
        &nbsp;Upload to Database
      </button>
      <BaseButton type=":word" @baseButtonClick="createJSONFile">
        <svg class="ex-icon">
          <use :xlink:href="'#share'"></use>
        </svg>
        &nbsp;Export Data
      </BaseButton>
      <BaseButton type=":word" @baseButtonClick="displayModal">
        <svg class="ex-icon">
          <use :xlink:href="'#refresh'"></use>
        </svg>
        &nbsp;Restart
      </BaseButton>
    </div>
  </div>
</template>

<script lang="ts">
//External
import Papa from 'papaparse';

//Internal
import Columns from './definitions';

//State
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('seed');

type CSVFile = {
  newPartNumber: string;
  plotImpedance: object[];
  plotESR: object[];
}

import Vue from 'vue';
export default Vue.extend({
  props: ['enableCeButton'],
  data() {
    return {
      csvFile: {
        newPartNumber: "",
        plotImpedance: [],
        plotESR: [],
      } as CSVFile,
      cePropsEnabled: false
    }
  },
  computed: {
    ...mapState([
      'highlight',
      'parts',
    ]),
  },
  methods: {
    ...mapActions([
      'updateSimulationData',
      'removeCapacitorByPn',
      'setDisplayDeleteModal'
    ]),
    checkOverlayPartType(fileName) {
      //checks if the overlaid part type matches the current selected part type, based off the csv file name.
      switch (this.parts[this.highlight].capType) {
        case 'Tantalum':
          if (fileName[0] !== 'T') {
            return false;
          }
          break;
        case 'Aluminum Electrolytic':
          if (fileName[0] !== 'A') {
            return false;
          }
          break;
        case 'Aluminum':
          if (fileName[0] !== 'A') {
            return false;
          }
          break;
        case 'Ceramic':
          if (fileName[0] !== 'C' && fileName[0] !== 'L' && fileName[0] !== '0') {
            return false;
          }
          break;
        default:
          return true;
      }
      return true;
    },
    createJSONFile() {
      let fileData = {};
      this.parts[this.highlight].seedProps.forEach((element) => fileData[element.name] = element.value);
      const blob = new Blob([JSON.stringify(fileData)]);
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      let fileName = this.parts[this.highlight].seedProps.find(p => p.name === "KEMET_Part_Number").value;
      link.setAttribute('download', `${fileName}.json`);
      document.body.appendChild(link);
      link.click();
    },
    degreesToRadians(degrees) {
      let pi = Math.PI;
      return degrees * (pi / 180);
    },
    displayModal() {
      this.setDisplayDeleteModal(true);
    },
    loadCeProps() {
      this.$emit('on-setLoadCeProps', true);
    },
    async readFile(file) {
      this.$emit('clear-error');
      if (file.length < 1) {
        return;
      }
      this.csvFile.plotImpedance = [];
      this.csvFile.plotESR = [];
      const allowedformats = ["csv"];
      let name = file[0].name;
      let samePartType = await this.checkOverlayPartType(name);
      if (!samePartType) {
        this.$emit("handle-error", "The part type of the csv file and the selected part type must match.");
        return;
      } else {
        this.csvFile.newPartNumber = name.replace(/(-.*)*.csv$/, "");
        this.setStyle(name);
        // this.updateSimulationData({ title: "Base KEMET Part number", output: this.csvFile.newPartNumber });
        const fileExt = name.split(".").pop().toLowerCase();
        if (!allowedformats.includes(fileExt)) {
          this.$emit('handle-error', 'File must be csv.')
          throw new Error("file must be csv.");
        }
        let reader = new FileReader();
        reader.readAsText(file[0]);
        reader.onload = async (event) => {
          if (!event.target) {
            return;
          }
          const csvData = event.target.result;
          const data = await Papa.parse(csvData, { dynamicTyping: true, skipEmptyLines: true });
          for (let value in data.data) {
            let tempImpedance;
            let convertedPhase;
            for (let i = 0; i < data.data[value].length; i++) {
              //check if the csv file has 3 columns
              if (data.data[value].length < 3) {
                this.$emit("handle-error", "The file must have 3 columns.");
                return;
              } else if (data.data[value].length > 3) {
                this.$emit("handle-error", "The file can only have 3 columns.");
                return;
              }
              switch (i) {
                case Columns.Frequency:
                  break;
                case Columns.Impedance:
                  tempImpedance = data.data[value][i];
                  break;
                case Columns.Phase:
                  convertedPhase = this.degreesToRadians(data.data[value][i]);
                  break;
                default:
                  console.log('error')
              }
            }
            this.csvFile.plotImpedance.push({
              x: data.data[value][Columns.Frequency],
              y: data.data[value][Columns.Impedance],
            });
            this.csvFile.plotESR.push({
              x: data.data[value][Columns.Frequency],
              y: (tempImpedance * Math.cos(convertedPhase))
            });
          }
          this.$emit('on-overlay', this.csvFile.plotImpedance, this.csvFile.plotESR);
        }
      }
    },
    setStyle(fileName) {
      //sets style for all parts based off of the csv file name
      //sets case size and series for aluminum/tantalum parts based off of csv file name
      const pnFromCsv = this.csvFile.newPartNumber || fileName;
      const upperName = fileName.toUpperCase();
      let style = this.parts[this.highlight].seedProps.find(p => p.name === "Style").value;
      const prefix3 = upperName.slice(0, 3); // ex: C4A, R74, R75...
      const prefix4 = upperName.slice(0, 4); // ex: C4AQ, C4AF, F862...
      const capType = this.parts[this.highlight].capType;
      switch (this.parts[this.highlight].capType) {
        case 'Tantalum':
        case 'Aluminum':
          style = fileName.slice(0, 5);
          let caseSize = style.slice(4, 5);
          let componentSeries = style.slice(0, 4);
          this.updateSimulationData({ title: "Case size", output: caseSize });
          this.updateSimulationData({ title: "Component series", output: componentSeries });
          break;
        case 'Film':
          // --- R7x 帶 H 的特例 ---
          if (['R74', 'R75', 'R76'].includes(prefix3)) {
            const matchChar = (pnFromCsv.toUpperCase())[pnFromCsv.length - 3];
            style = prefix3 + (matchChar === 'H' ? 'H' : '');
          }
          // --- 其他已知特例 ---
          else if (prefix3 === 'R41') {
            style = 'R41T';
          }
          else if (prefix4 === 'F862') {
            style = 'F862';
          }
          // --- ✅ C4A* 系列一律取 4 碼（關鍵修正） ---
          else if (prefix4.startsWith('C4A')) {
            style = prefix4; // 例如 C4AQ、C4AF、C4AK...
          }
          else if (prefix3 === 'C44') {
            // 歷史例外你程式原本就有，保留
            style = 'C44P R';
          }
          else {
            // 預設盡量用 4 碼，避免 C4A 這種不完整
            style = prefix4;
          }

          // ✅ 用 title: "Style" 寫回（你的 action 用 title 對應）
          this.updateSimulationData({ title: "Style", output: style });

          // 下面兩行視你的表單欄位命名而定（保險起見可一併打）
          this.updateSimulationData({ title: "Component series", output: style });
          this.updateSimulationData({ title: "Capacitor series", output: style });

          break;
        case 'Ceramic':
          if (fileName[0] === 'C') {
            if (prefix3 !== 'CAN' && prefix3 !== 'CKC') {
              style = fileName.slice(0, 5);
            }
          } else if (fileName[0] === 'L') {
            if (prefix3 === 'L1X') {
              let appendStyle = fileName.slice(4, 6);
              style = 'L1x_' + appendStyle;
            } else {
              style = fileName.slice(0, 5);
            }
          }
          break;
        case 'Aluminum Electrolytic':
          style = fileName.slice(0, 4);
          break;
      }
      this.updateSimulationData({ title: "Capacitor series", output: style });
    }
  }
});
</script>

<style lang="css" scoped>
.button-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

[class*='e-button']:active, [class*='e-button']:focus {
  background-size: 180%;
}
</style>